import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { result, truncate, values } from 'libraries/lodash'
import cx from 'classnames'
import linkify from 'linkifyjs/html'
import dayjs from 'dayjs'
import ArrowForward from '@material-ui/icons/ArrowForward'
import Grid from '@material-ui/core/Grid'
import MuiCard from '@material-ui/core/Card'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import url from 'front-end-core/core/url'
import connect from 'front-end-core/decorators/connect'
import { DISPLAY_DATE_FORMAT } from 'constants/locale'
import { MW_ANALYTICS, MW_DEAL_DETAILS, MW_LIST_DEALS } from 'constants/controllers'
import { URL_MW_SCOPE_OF_SERVICE } from 'constants/urls'
import {
  requestEventTypes,
  requestFeaturedDeal,
  requestLatestDeals,
  requestStatsSummary
} from 'services/DealwatchService'
import { dismissPopover } from 'services/OverlayService'
import useBoundingClientRect from 'hooks/useBoundingClientRect'
import selectAolName from 'redux/selectors/selectAolName'
import ArrowLink from 'components/dealwatch/ArrowLink'
import ResponseConsumer from 'components/shared/ResponseConsumer'

const Overview = ({
  eventTypes,
  featuredDeal,
  stats,
  deals: { results: deals = [] },
  browser,
  isLogged,
  setSignUpModalIsOpen
}) => {
  const handlePrePayOnClick = (e) => {
    dismissPopover(e.target)
    if (!isLogged) {
      e.preventDefault()
      e.stopPropagation()
      setSignUpModalIsOpen(true)
    }
  }

  return (
    <div className='bg-ghost-grey vw-100-t w-100'>
      <div className='layout-width-constraint mv8'>
        <Grid container spacing={2}>
          <Grid item xs={12} className='deal-types-wrapper'>
            <BlockLinks {...{ eventTypes, handlePrePayOnClick }} />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={8}>
                <Grid container spaacing={2}>
                  <Grid item xs={12} className='featured-deals-wrapper'>
                    <BlockCommentary {...{
                      featuredDeal,
                      eventTypes,
                      handlePrePayOnClick,
                      browser
                    }}
                    />
                  </Grid>
                  <Grid item xs={12} className='recent-deals-wrapper'>
                    <BlockRecentDeals {...{ eventTypes, deals, handlePrePayOnClick }} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <div className='bg-twilight-grey ph6 pt6 pb9'>
                  <BlockStats {...{ stats, handlePrePayOnClick }} />
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </div>
  )
}

const BlockLinks = ({ eventTypes, className, handlePrePayOnClick }) => {
  const eventTypesList = values(eventTypes)

  const tiles = [
    <Tile
      key='0'
      name='All Deals'
      type='all'
      label='[Homepage][All Deals]'
      {...{ handlePrePayOnClick }}
    />
  ]

  eventTypesList.forEach((type, key) =>
    tiles.push(
      <Tile
        key={key + 1}
        {...type}
        label={type.name}
        {...{ handlePrePayOnClick }}
      />
    )
  )
  return (
    <Grid container className={className} spacing={2}>
      {tiles}
    </Grid>
  )
}

const BlockStats = ({ stats, handlePrePayOnClick }) => {
  const aol = useSelector(selectAolName)

  return (
    <Grid container direction='column' spacing={2}>
      <Grid item className='pv4'>
        <UserGuideLink {...{ handlePrePayOnClick }} />
      </Grid>
      <Grid item>
        <div className='white bg-dark-sea-green-3 f7 pa4'>
          Deal activity{' '}
          {dayjs().subtract(1, 'month').format(DISPLAY_DATE_FORMAT)}{' '}
          to today
        </div>
      </Grid>
      {stats.map((stat, index) =>
        <Grid item key={index}>
          <InfoBox {...stat} />
        </Grid>)}
      <Grid item>
        <div className='ml4 mt4 relative'>
          <div className='absolute left0' onClick={handlePrePayOnClick}>
            <ArrowLink
              text='See all analytics'
              url={url(MW_ANALYTICS, { aol })}
              textCx='cadet-blue no-underline'
              dataEvent={{ category: 'Markets:Menu', action: 'Navigation', label: '[Homepage] to [Analytics]' }}
            />
          </div>
        </div>
      </Grid>
    </Grid>
  )
}

const Deal = ({ aol, deal, handlePrePayOnClick, eventTypes }) => (
  <div onClick={handlePrePayOnClick} className='bb b--phantom-grey'>
    <a
      className='no-underline black flex pv4 ph5 hover-bg-phantom-grey'
      href={url(MW_DEAL_DETAILS, { aol, dealId: deal.dealId })}
      data-event-category='Markets:Deal'
      data-event-action='Navigation'
      data-event-label={`[Homepage] to [${deal.title}]`}
    >
      <span className='w12 mr6'>
        {result(eventTypes, [deal.dealType, 'name'], '-')}
      </span>
      <span className='truncate w15'>{deal.title}</span>
      <span className='w11'>
        {deal.primaryDate.format('DD/MM/YYYY')}
      </span>
    </a>
  </div>
)

const BlockRecentDeals = ({ deals, eventTypes, handlePrePayOnClick }) => {
  const aol = useSelector(selectAolName)

  return (
    <div className='twilight-grey pa4 flex flex-column mt9 mb6'>
      <h2 className='f3 b mb6 ml6'>
        Recent Deals by Deal Type
      </h2>
      <div>
        <div className='flex bg-white b pv3 bt-thick bb-thin b--dark-sea-green-3'>
          <div className='w12 ml5 mr6'>Deal Type</div>
          <div className='truncate w15 pr6'>Deal</div>
          <div className='w11 mr5'>Deal Date</div>
        </div>
        {deals.map((deal, index) => <Deal key={index} {...{ aol, deal, eventTypes, handlePrePayOnClick }} />)}
        <div className='ml7 mt7'>
          <div className='relative'>
            <div className='absolute' onClick={handlePrePayOnClick}>
              <ArrowLink
                text='See all deals'
                url={url(MW_LIST_DEALS, { aol, dealType: 'all' })}
                textCx='cadet-blue no-underline'
                dataEvent={{ category: 'Markets:Deal', action: 'Navigation', label: '[Homepage] to [All Deals]' }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const BlockCommentary = ({ browser, featuredDeal, eventTypes, handlePrePayOnClick }) => {
  const [rect, ref] = useBoundingClientRect()
  const aol = useSelector(selectAolName)

  if (!featuredDeal) return null

  return (
    <div ref={ref}>
      <div className='speech-bubble'>
        <div className='b f4 mb6'>FromCounsel&apos;s Top Pick and Commentary</div>
        {browser.name === 'ie' && rect !== null && !rect.width
          ? null
          : [
            <p key='name'>
              <span onClick={handlePrePayOnClick}>
                <a
                  className='no-underline hover-underline'
                  href={url(MW_DEAL_DETAILS, { aol, dealId: featuredDeal.dealId })}
                  data-event-category='Markets:Deal'
                  data-event-action='Navigation'
                  data-event-label={`[Homepage] to [${featuredDeal.dealName}]`}
                >
                  {featuredDeal.dealName}
                </a>
              </span>
              <br />
              {result(eventTypes, [featuredDeal.dealType, 'name'])}
            </p>,
            truncate(featuredDeal.dealCommentary, { length: 400 })
              .split('\n')
              .map((paragraph, key) => (
                <p
                  key={key}
                  dangerouslySetInnerHTML={{
                    __html: linkify(paragraph, { target: '_blank', rel: 'noopener noreferrer' })
                  }}
                />
              )),
            <ReadFullStoryLink
              key='read-full-story-link'
              {...{ featuredDeal, handlePrePayOnClick, aol }}
            />
            ]}
      </div>
    </div>
  )
}

const ReadFullStoryLink = ({ featuredDeal: { dealName, dealId }, handlePrePayOnClick, aol }) => (
  <div className='ml4 mv4 flex'>
    <div className='relative' onClick={handlePrePayOnClick}>
      <ArrowLink
        text='Read full story'
        url={url(MW_DEAL_DETAILS, { aol, dealId })}
        textCx='cadet-blue no-underline'
        dataEvent={{ category: 'Markets:Deal', action: 'Navigation', label: `[Homepage] to [${dealName}]` }}
      />
    </div>
  </div>
)

const UserGuideLink = ({ handlePrePayOnClick }) => (
  <div onClick={handlePrePayOnClick} className='flex items-center'>
    <InfoOutlinedIcon />
    <a
      href={URL_MW_SCOPE_OF_SERVICE}
      className='no-underline hover-underline tl cadet-blue ml4'
      target='_blank'
      rel='noopener noreferrer'
      data-event-category='Legal'
      data-event-action='PDF'
      data-event-label='Markets [Homepage] Scope of service'
    >
      Scope of service
    </a>
  </div>
)

const InfoBox = ({ description, up, down, title, value }) => {
  const pctIndex = description.indexOf('%') + 1
  const pctChange = description.substr(0, pctIndex)
  description = description.substr(pctIndex)

  return (
    <MuiCard className='bg-white pl3 pt2 br0 bb mb0 b--dark-cyan h6'>
      <div className='b ml2 f5'>{title}</div>
      <div className='flex pa0 items-center b ml5 f4'>
        {up && <span className='w0 h0 mr2 deal-increase' />}
        {down && <span className='w0 h0 mr2 deal-decrease' />}
        <div className='vm ml4'>{value}</div>
      </div>
      <div className='cadet-blue -mt1 pt0 pb2 ph5 mt1 f6'>
        <span className={cx({
          'dark-sea-green': up,
          crimson: down
        })}
        >
          {pctChange}
        </span>
        <span>
          {description}
        </span>
      </div>
    </MuiCard>
  )
}

const Tile = ({ type, label, name, handlePrePayOnClick }) => {
  const [hover, setHover] = useState(false)
  const aol = useSelector(selectAolName)

  return (
    <Grid
      item xs={12} sm={6} md={3}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={handlePrePayOnClick}
    >
      <a
        className='justify-between h7 relative b no-underline black pointer flex flex-column pa6 bb-thick b--dark-sea-green-3 bg-white shadow-black-4 markets-overview-tile'
        href={url(MW_LIST_DEALS, { aol, dealType: type })}
        data-event-category='Markets:Menu'
        data-event-action='Navigation'
        data-event-label={label}
      >
        <div className='black'>{name}</div>
        <ArrowForward
          className={cx('w7 ml6 self-end', {
            'dark-cyan': hover,
            'cadet-blue': !hover
          })}
        />
      </a>
    </Grid>
  )
}

const ConnectedOverview = connect(() => ({
  eventTypes: requestEventTypes(),
  featuredDeal: requestFeaturedDeal(),
  stats: requestStatsSummary(),
  deals: requestLatestDeals()
}))(props => (
  <ResponseConsumer
    content={() => <Overview {...props} />}
    responses={[props.eventTypes, props.featuredDeal, props.stats, props.deals]}
  />
))

export default ConnectedOverview
